import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FiUser, FiDollarSign, FiTrendingUp, FiInfo, FiSmartphone, FiShield } from 'react-icons/fi';
import { toast } from 'sonner';
import { formatBRL } from '../utils/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Perfil = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    cpf: '',
    occupation: '',
    cep: '',
    address: '',
    phone: '',
    monthly_salary: '',
    salary_payment_day: '',
    extra_income: '',
    extra_income_payment_day: '',
    extra_income_description: ''
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`, {
        withCredentials: true
      });
      setFormData({
        full_name: response.data.full_name || '',
        cpf: response.data.cpf || '',
        occupation: response.data.occupation || '',
        cep: response.data.cep || '',
        address: response.data.address || '',
        phone: response.data.phone || '',
        monthly_salary: response.data.monthly_salary || '',
        salary_payment_day: response.data.salary_payment_day || '',
        extra_income: response.data.extra_income || '',
        extra_income_payment_day: response.data.extra_income_payment_day || '',
        extra_income_description: response.data.extra_income_description || ''
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Erro ao carregar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleCepBlur = async () => {
    const cepClean = formData.cep.replace(/\D/g, '');
    if (cepClean.length === 8) {
      setLoadingCep(true);
      try {
        const response = await axios.get(`${API}/cep/${cepClean}`, {
          withCredentials: true
        });
        setFormData(prev => ({
          ...prev,
          address: response.data.full_address
        }));
        toast.success('Endereço encontrado! Complete com o número.');
      } catch (error) {
        toast.error('CEP não encontrado');
      } finally {
        setLoadingCep(false);
      }
    }
  };

  const formatPhone = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3').replace(/-$/, '');
    }
    return cleaned.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3').replace(/-$/, '');
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setFormData({ ...formData, phone: formatted });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...formData,
        monthly_salary: parseFloat(formData.monthly_salary || 0),
        salary_payment_day: formData.salary_payment_day ? parseInt(formData.salary_payment_day) : null,
        extra_income: parseFloat(formData.extra_income || 0),
        extra_income_payment_day: formData.extra_income_payment_day ? parseInt(formData.extra_income_payment_day) : null
      };

      await axios.put(`${API}/profile`, data, {
        withCredentials: true
      });

      toast.success('Perfil atualizado com sucesso!');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Erro ao salvar perfil');
    } finally {
      setSaving(false);
    }
  };

  const loadSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubInfo(response.data);
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setSubLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
            <p className="mt-4 text-blue-900 font-medium">Carregando...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
      <Header />
      
      <main className="transition-all duration-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-black text-blue-900 mb-1">Meu Perfil</h1>
            <p className="text-slate-700">Gerencie seus dados pessoais e informações financeiras</p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/sessoes-ativas')}
            className="border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center gap-2 self-start sm:self-auto shadow-sm"
          >
            <FiShield size={16} className="text-cyan-600" />
            <span>Sessões Ativas</span>
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Dados Pessoais */}
          <Card data-testid="personal-data-card" className="bg-white border border-blue-900/10 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl font-semibold text-blue-900">
                <FiUser size={24} />
                Dados Pessoais
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="full_name" className="text-blue-900 font-semibold">Nome / Razão Social</Label>
                  <Input
                    id="full_name"
                    data-testid="full-name-input"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="mt-1"
                    placeholder="Seu nome ou razão social"
                  />
                </div>
                
                <div>
                  <Label htmlFor="cpf" className="text-blue-900 font-semibold">CPF / CNPJ</Label>
                  <Input
                    id="cpf"
                    data-testid="cpf-input"
                    value={formData.cpf}
                    onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                    className="mt-1"
                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="occupation" className="text-blue-900 font-semibold">Profissão</Label>
                <Input
                  id="occupation"
                  data-testid="occupation-input"
                  value={formData.occupation}
                  onChange={(e) => setFormData({ ...formData, occupation: e.target.value })}
                  className="mt-1"
                  placeholder="Ex: Analista Contábil"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="cep" className="text-blue-900 font-semibold">CEP</Label>
                  <Input
                    id="cep"
                    data-testid="cep-input"
                    value={formData.cep}
                    onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                    onBlur={handleCepBlur}
                    className="mt-1"
                    placeholder="00000-000"
                    maxLength={9}
                  />
                  {loadingCep && <p className="text-xs text-blue-900 mt-1">Buscando...</p>}
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="address" className="text-blue-900 font-semibold">Endereço</Label>
                  <Input
                    id="address"
                    data-testid="address-input"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="mt-1"
                    placeholder="Rua, número, bairro, cidade - UF"
                  />
                  <p className="text-xs text-slate-600 mt-1">Complete com o número da residência</p>
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="text-blue-900 font-semibold">Telefone</Label>
                <Input
                  id="phone"
                  data-testid="phone-input"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  className="mt-1"
                  placeholder="(00) 00000-0000"
                  maxLength={15}
                />
              </div>
            </CardContent>
          </Card>

          {/* Informações Financeiras */}
          <Card data-testid="financial-data-card" className="bg-white border border-blue-900/10 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl font-semibold text-blue-900">
                <FiDollarSign size={24} />
                Informações Financeiras
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="monthly_salary" className="text-blue-900 font-semibold">Salário Mensal (R$)</Label>
                <Input
                  id="monthly_salary"
                  data-testid="salary-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.monthly_salary}
                  onChange={(e) => setFormData({ ...formData, monthly_salary: e.target.value })}
                  className="mt-1"
                  placeholder="0.00"
                />
                <p className="text-xs text-slate-600 mt-1">Sua receita fixa mensal</p>
              </div>

              <div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="salary_payment_day" className="text-blue-900 font-semibold">Dia que recebe o salário</Label>
                  <Popover>
                    <PopoverTrigger>
                      <FiInfo className="text-slate-500 hover:text-blue-900 cursor-help" size={16} />
                    </PopoverTrigger>
                    <PopoverContent className="text-sm">
                      <p className="font-semibold text-blue-900 mb-1">Como funciona</p>
                      <p>Você pode informar de <strong>1 a 31</strong>. Se o mês não tiver o dia escolhido (ex: dia 31 em fevereiro), o registro será gerado automaticamente no <strong>último dia do mês</strong>.</p>
                    </PopoverContent>
                  </Popover>
                </div>
                <Input
                  id="salary_payment_day"
                  data-testid="salary-day-input"
                  type="number"
                  min="1"
                  max="31"
                  value={formData.salary_payment_day}
                  onChange={(e) => {
                    const val = Math.min(Math.max(parseInt(e.target.value) || 0, 0), 31);
                    setFormData({ ...formData, salary_payment_day: val > 0 ? val : '' });
                  }}
                  className="mt-1"
                  placeholder="Ex: 5"
                />
                <p className="text-xs text-slate-600 mt-1">Dia do mês (1 a 31). Meses curtos usam o último dia.</p>
              </div>

              <div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="extra_income" className="text-blue-900 font-semibold">Renda Extra (R$)</Label>
                  <Popover>
                    <PopoverTrigger>
                      <FiInfo className="text-slate-500 hover:text-blue-900 cursor-help" size={16} />
                    </PopoverTrigger>
                    <PopoverContent className="text-sm">
                      <p className="font-semibold text-blue-900 mb-1">Renda Extra Fixa</p>
                      <p>Este valor será adicionado como <strong>receita fixa</strong> todo mês no seu Dashboard.</p>
                      <p className="mt-2 text-xs text-slate-600">Se a renda for <strong>variável</strong>, não preencha aqui. Crie um registro de "Conta a Receber" mês a mês.</p>
                    </PopoverContent>
                  </Popover>
                </div>
                <Input
                  id="extra_income"
                  data-testid="extra-income-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.extra_income}
                  onChange={(e) => setFormData({ ...formData, extra_income: e.target.value })}
                  className="mt-1"
                  placeholder="0.00"
                />
              </div>

              <div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="extra_income_payment_day" className="text-blue-900 font-semibold">Dia que recebe a renda extra</Label>
                  <Popover>
                    <PopoverTrigger>
                      <FiInfo className="text-slate-500 hover:text-blue-900 cursor-help" size={16} />
                    </PopoverTrigger>
                    <PopoverContent className="text-sm">
                      <p className="font-semibold text-blue-900 mb-1">Como funciona</p>
                      <p>Você pode informar de <strong>1 a 31</strong>. Se o mês não tiver o dia escolhido (ex: dia 31 em fevereiro), o registro será gerado automaticamente no <strong>último dia do mês</strong>.</p>
                    </PopoverContent>
                  </Popover>
                </div>
                <Input
                  id="extra_income_payment_day"
                  data-testid="extra-income-day-input"
                  type="number"
                  min="1"
                  max="31"
                  value={formData.extra_income_payment_day}
                  onChange={(e) => {
                    const val = Math.min(Math.max(parseInt(e.target.value) || 0, 0), 31);
                    setFormData({ ...formData, extra_income_payment_day: val > 0 ? val : '' });
                  }}
                  className="mt-1"
                  placeholder="Ex: 15"
                />
                <p className="text-xs text-slate-600 mt-1">Dia do mês (1 a 31). Meses curtos usam o último dia.</p>
              </div>

              <div>
                <Label htmlFor="extra_income_description" className="text-blue-900 font-semibold">Descrição da Renda Extra</Label>
                <Textarea
                  id="extra_income_description"
                  data-testid="extra-income-desc-input"
                  value={formData.extra_income_description}
                  onChange={(e) => setFormData({ ...formData, extra_income_description: e.target.value })}
                  className="mt-1"
                  placeholder="Ex: Fundos Imobiliários, Freelance, Aluguel..."
                  rows={3}
                />
              </div>

              {(parseFloat(formData.monthly_salary) + parseFloat(formData.extra_income || 0)) > 0 && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FiTrendingUp className="text-blue-900" size={20} />
                    <p className="text-sm font-bold text-blue-900">Receita Total Mensal</p>
                  </div>
                  <p className="text-2xl font-black text-blue-900">
                    {formatBRL(parseFloat(formData.monthly_salary || 0) + parseFloat(formData.extra_income || 0))}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => loadProfile()}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              data-testid="save-profile-button"
              type="submit"
              disabled={saving}
              className="bg-blue-900 hover:bg-blue-800 text-white"
            >
              {saving ? 'Salvando...' : 'Salvar Perfil'}
            </Button>
          </div>
        </form>
      </div>
      </main>
    </div>
  );
};
